import { CdkDragEnd, DragDropModule } from '@angular/cdk/drag-drop';

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  InjectionToken,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import {
  AiChatWindowService,
  AiChatWindowState,
} from '../../services/ai-chat-window.service';
import { AiChatComponent } from '../ai-chat/ai-chat.component';

export interface AiChatWindowData {
  id: string;
  title: string;
  width: number;
  height: number;
}

export const AI_CHAT_WINDOW_DATA = new InjectionToken<AiChatWindowData>(
  'AI_CHAT_WINDOW_DATA'
);

@Component({
  selector: 'qt-ai-chat-window',
  standalone: true,
  imports: [
    DragDropModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    TranslationCoreModule,
    AiChatComponent,
  ],
  templateUrl: './ai-chat-window.component.html',
  styleUrls: ['./ai-chat-window.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatWindowComponent {
  boundary = 'body';
  dragPosition = { x: 0, y: 0 };

  // Signals for reactive state management
  private readonly currentPosition = signal({ x: 0, y: 0 });
  private readonly state = signal<AiChatWindowState>('normal');

  // Computed signals
  readonly maximized = computed(() => this.state() === 'max');
  readonly minimized = computed(() => this.state() === 'min');

  // Signal-based injections
  public readonly data = inject(AI_CHAT_WINDOW_DATA);
  private readonly windows = inject(AiChatWindowService);
  private readonly cdr = inject(ChangeDetectorRef);

  close(): void {
    this.windows.close(this.data.id);
  }

  toggleMinimize(): void {
    this.windows.toggleMinimize(this.data.id);
  }

  toggleMax(): void {
    this.windows.toggleMaximize(this.data.id);
  }

  handleDragEnd(event: CdkDragEnd): void {
    if (this.maximized()) {
      event.source.reset();
      return;
    }

    const delta = event.source.getFreeDragPosition();
    event.source.reset();

    const currentPos = this.currentPosition();
    const absolute = {
      x: currentPos.x + delta.x,
      y: currentPos.y + delta.y,
    };

    const clamped = this.windows.updatePosition(this.data.id, absolute);
    this.setPosition(clamped);
  }

  setPosition(pos: { x: number; y: number }): void {
    this.currentPosition.set({ ...pos });
    this.dragPosition = { x: 0, y: 0 };
    this.cdr.markForCheck();
  }

  setState(state: AiChatWindowState): void {
    this.state.set(state);
    this.cdr.markForCheck();
  }
}
